import { ListingPlatform } from "./types";
import type { PropertyStatus, PropertyListItem, PropertyDetail } from "./types";

/** Re-exported from `shared/lib` — the canonical, locale-aware date formatter. */
export { formatDateShort } from "@/shared/lib/format-date";

/** Translation key for a property status badge. Copy under `properties.statusLabel.<STATUS>`. */
export function propertyStatusLabelKey(status: PropertyStatus): string {
  return `properties.statusLabel.${status}`;
}

/** Badge color intent per status — keeps the badge component dumb. */
export function propertyStatusVariant(
  status: PropertyStatus,
): "default" | "secondary" | "outline" | "success" {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "TRIAL":
    case "PAUSED_NO_VENUES":
      return "outline";
    case "INACTIVE":
    case "SUSPENDED":
      return "secondary";
    default:
      return "default";
  }
}

/**
 * Single-line address for a list row / detail header. Joins the present
 * parts and skips blanks so we never render "City, , US".
 */
export function formatPropertyLocation(
  property: Pick<PropertyListItem | PropertyDetail, "address" | "city" | "state" | "country">,
): string {
  return [property.address, property.city, property.state, property.country]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part && part.length > 0))
    .join(", ");
}

/**
 * Human label for a listing platform. Known platforms get a translated label
 * under `properties.detail.listingPlatform.<PLATFORM>`; an unknown value (the
 * API may add platforms later) falls back to the raw key so nothing renders blank.
 */
export function formatListingPlatform(platform: string, t: (key: string) => string): string {
  const known = (Object.values(ListingPlatform) as string[]).includes(platform);
  return known ? t(`properties.detail.listingPlatform.${platform}`) : platform;
}
